import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { fetchUserRolesForPermission, fetchUserRolesWithPermissionsCached, getCachedUserRolesResponse, markRolesSessionHydrated } from '../utils/userRolesCache';

const RoleContext = createContext();

const normalizeRoleId = (roleId) => String(roleId ?? "").trim();

const readStoredPermissionsMap = () => {
  try {
    const raw = localStorage.getItem("role_permissions_map");
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const readStoredUserPermissions = () => {
  try {
    const raw = localStorage.getItem("user_permissions");
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getPermissionsForRoleId = (roleId, permissionsMap = {}) => {
  const normalizedId = normalizeRoleId(roleId);
  if (!normalizedId) return [];
  if (Array.isArray(permissionsMap[normalizedId])) {
    return permissionsMap[normalizedId];
  }
  const matchKey = Object.keys(permissionsMap).find(
    (key) => normalizeRoleId(key) === normalizedId
  );
  return matchKey && Array.isArray(permissionsMap[matchKey])
    ? permissionsMap[matchKey]
    : [];
};

const mergePermissionsMaps = (...maps) => {
  const merged = {};
  maps.forEach((map) => {
    if (!map || typeof map !== "object") return;
    Object.entries(map).forEach(([roleId, permissions]) => {
      const id = normalizeRoleId(roleId);
      if (!id || !Array.isArray(permissions)) return;
      merged[id] = permissions;
    });
  });
  return merged;
};

const persistActiveRolePermissions = (roleId, permissionsMap) => {
  const id = normalizeRoleId(roleId);
  if (!id) return [];
  const permissions = getPermissionsForRoleId(id, permissionsMap);
  if (permissions.length > 0) {
    localStorage.setItem("user_permissions", JSON.stringify(permissions));
  }
  localStorage.setItem("active_role_id", id);
  localStorage.setItem("user_role", id);
  return permissions;
};

export const useRole = () => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
};

const dispatchRoleChanged = (roleId, role = null) => {
  window.dispatchEvent(
    new CustomEvent("roleChanged", {
      detail: { roleId: normalizeRoleId(roleId), role },
    })
  );
};

export const RoleProvider = ({ children }) => {
  const [userRoles, setUserRoles] = useState([]);
  const [activeRole, setActiveRole] = useState(null);
  const [rolePermissions, setRolePermissions] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [permissionsRevision, setPermissionsRevision] = useState(0);
  const rolesFetchPromiseRef = useRef(null);

  const applyRolePermissionsState = useCallback((roleId, incomingMap = {}) => {
    const normalizedRoleId = normalizeRoleId(roleId);
    if (!normalizedRoleId) return [];

    let appliedPermissions = [];
    setRolePermissions((prev) => {
      const merged = mergePermissionsMaps(
        readStoredPermissionsMap(),
        prev,
        incomingMap
      );
      localStorage.setItem("role_permissions_map", JSON.stringify(merged));
      appliedPermissions = persistActiveRolePermissions(normalizedRoleId, merged);
      return merged;
    });
    setPermissionsRevision((value) => value + 1);
    return appliedPermissions;
  }, []);

  const fetchUserRolesAndPermissions = useCallback(async (options = {}) => {
    const { force = false } = options;
    const accessToken = localStorage.getItem('accessToken');
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    
    if (!accessToken || !userId) {
      setIsLoading(false);
      return;
    }

    const storedRoles = localStorage.getItem('user_roles_data');
    const storedPermissions = localStorage.getItem('role_permissions_map');
    let hasStoredPermissions = false;
    try {
      hasStoredPermissions =
        Boolean(storedPermissions) &&
        storedPermissions !== '{}' &&
        Object.keys(JSON.parse(storedPermissions || '{}')).length > 0;
    } catch {
      hasStoredPermissions = false;
    }
    if (!hasStoredPermissions) {
      hasStoredPermissions = readStoredUserPermissions().length > 0;
    }

    if (!force && storedRoles && hasStoredPermissions) {
      const activeRoleId =
        localStorage.getItem("active_role_id") ||
        localStorage.getItem("user_role");
      const activeRolePerms = getPermissionsForRoleId(
        activeRoleId,
        readStoredPermissionsMap()
      );
      const flatPerms = readStoredUserPermissions();
      // Only skip the API when the active role actually has permissions.
      // An empty map entry (e.g. login overwrote prefetch) must be refreshed.
      if (activeRolePerms.length > 0 || flatPerms.length > 0) {
        loadRolesFromStorage();
        markRolesSessionHydrated(userId);
        setIsLoading(false);
        return;
      }
    }

    if (rolesFetchPromiseRef.current) {
      return rolesFetchPromiseRef.current;
    }

    rolesFetchPromiseRef.current = (async () => {
      try {
        const result = force
          ? await fetchUserRolesForPermission(userId, accessToken)
          : (getCachedUserRolesResponse(userId, accessToken) ||
            (await fetchUserRolesForPermission(userId, accessToken)));

        if (!result) {
          return;
        }

        const { ok, status, data } = result;

        if (status === 404) {
          setUserRoles([]);
          return;
        }

        if (ok) {
          if (data.success && data.data) {
            let formattedRoles = [];
            
            if (data.data.roles && Array.isArray(data.data.roles) && data.data.roles.length > 0) {
              formattedRoles = data.data.roles.map(item => ({
                id: (item.role_id || item.id).toString(),
                name: item.name || `Role ${item.role_id || item.id}`
              }));
            } else if (Array.isArray(data.data) && data.data.length > 0) {
              formattedRoles = data.data.map(item => ({
                id: (item.role_id || item.id).toString(),
                name: item.name || `Role ${item.role_id || item.id}`
              }));
            }
            
            if (formattedRoles.length > 0) {
              setUserRoles(formattedRoles);
              localStorage.setItem('user_roles_data', JSON.stringify(formattedRoles));

              const mergedPermissionsMap = mergePermissionsMaps(
                readStoredPermissionsMap(),
                data.data.permissions && typeof data.data.permissions === "object"
                  ? data.data.permissions
                  : {}
              );
              if (Object.keys(mergedPermissionsMap).length > 0) {
                setRolePermissions(mergedPermissionsMap);
                localStorage.setItem(
                  "role_permissions_map",
                  JSON.stringify(mergedPermissionsMap)
                );
              }

              const currentActiveRoleId =
                localStorage.getItem("active_role_id") || formattedRoles[0].id;
              const active =
                formattedRoles.find(
                  (r) =>
                    normalizeRoleId(r.id) === normalizeRoleId(currentActiveRoleId)
                ) || formattedRoles[0];
              setActiveRole(active);
              applyRolePermissionsState(active.id, mergedPermissionsMap);
              dispatchRoleChanged(active.id, active);

              const activeRoleHasPerms =
                getPermissionsForRoleId(active.id, mergedPermissionsMap).length > 0;
              const shouldFetchPermissions =
                force ||
                !hasStoredPermissions ||
                Object.keys(mergedPermissionsMap).length === 0 ||
                !activeRoleHasPerms;

              if (shouldFetchPermissions) {
                await fetchUserPermissionsForRoles(
                  formattedRoles,
                  accessToken,
                  token,
                  userId,
                  null,
                  force || !activeRoleHasPerms
                );
              }
              applyRolePermissionsState(
                active.id,
                readStoredPermissionsMap()
              );
              dispatchRoleChanged(active.id, active);
              markRolesSessionHydrated(userId);
            } else {
              setUserRoles([]);
            }
          } else {
            setUserRoles([]);
          }
        } else {
          setUserRoles([]);
        }
      } catch (error) {
        console.error('Error fetching user roles:', error);
      } finally {
        setIsLoading(false);
        rolesFetchPromiseRef.current = null;
      }
    })();

    return rolesFetchPromiseRef.current;
  }, [applyRolePermissionsState]);

  useEffect(() => {
    loadRolesFromStorage();
    fetchUserRolesAndPermissions();

    const handleUserLogin = () => {
      loadRolesFromStorage();
      const loggedInUserId = localStorage.getItem('userId');
      if (loggedInUserId) {
        markRolesSessionHydrated(loggedInUserId);
      }
      fetchUserRolesAndPermissions({ force: true });
    };

    window.addEventListener('userLoggedIn', handleUserLogin);

    return () => {
      window.removeEventListener('userLoggedIn', handleUserLogin);
    };
  }, [fetchUserRolesAndPermissions]);

  const loadRolesFromStorage = () => {
    try {
      const storedRoles = localStorage.getItem('user_roles_data');
      const storedActiveRole = localStorage.getItem('active_role_id');
      const permissionsMap = readStoredPermissionsMap();

      if (Object.keys(permissionsMap).length > 0) {
        setRolePermissions(permissionsMap);
      }

      if (storedRoles) {
        const roles = JSON.parse(storedRoles);
        setUserRoles(roles);

        const active =
          storedActiveRole && roles.length > 0
            ? roles.find(
                (r) => normalizeRoleId(r.id) === normalizeRoleId(storedActiveRole)
              ) || roles[0]
            : roles[0];
        if (active) {
          setActiveRole(active);
          applyRolePermissionsState(active.id, permissionsMap);
        }
      } else {
        const currentRoleId =
          localStorage.getItem('active_role_id') ||
          localStorage.getItem('user_role');
        if (currentRoleId) {
          const singleRole = {
            id: normalizeRoleId(currentRoleId),
            name: `Role ${currentRoleId}`,
          };
          setUserRoles([singleRole]);
          setActiveRole(singleRole);
          applyRolePermissionsState(singleRole.id, permissionsMap);
        }
      }

      if (readStoredUserPermissions().length === 0) {
        const activeRoleId =
          localStorage.getItem("active_role_id") ||
          localStorage.getItem("user_role");
        if (activeRoleId) {
          applyRolePermissionsState(activeRoleId, permissionsMap);
        }
      }
    } catch (error) {
      console.error('Error loading roles from storage:', error);
    }
  };

  // Fetch permissions for user's roles separately
  const fetchUserPermissionsForRoles = async (roles, token, accessToken, userId, specificRoleId = null, force = false) => {
    try {
      const { response, data } = await fetchUserRolesWithPermissionsCached(accessToken, {
        userId,
        roleId: specificRoleId,
        force,
      }) || {};

      if (response?.ok && data?.success && data?.data?.permissions) {
          const mergedPermissionsMap = mergePermissionsMaps(
            readStoredPermissionsMap(),
            data.data.permissions || {}
          );
          setRolePermissions(mergedPermissionsMap);
          localStorage.setItem(
            "role_permissions_map",
            JSON.stringify(mergedPermissionsMap)
          );

          const roleId =
            specificRoleId ||
            localStorage.getItem("active_role_id") ||
            localStorage.getItem("user_role");
          if (roleId) {
            persistActiveRolePermissions(roleId, mergedPermissionsMap);
          }
          setPermissionsRevision((value) => value + 1);
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  };

  const switchRole = async (roleId) => {
    const normalizedRoleId = normalizeRoleId(roleId);
    const role = userRoles.find(
      (r) => normalizeRoleId(r.id) === normalizedRoleId
    );
    if (role) {
      setActiveRole(role);
      localStorage.setItem("active_role_id", normalizedRoleId);
      localStorage.setItem("user_role", normalizedRoleId);
      const userIdForLastRole = localStorage.getItem('userId');
      if (userIdForLastRole) {
        localStorage.setItem(`last_active_role_${userIdForLastRole}`, normalizedRoleId);
      }

      applyRolePermissionsState(normalizedRoleId, readStoredPermissionsMap());
      dispatchRoleChanged(normalizedRoleId, role);

      const accessToken = localStorage.getItem('accessToken');
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      
      if (accessToken && userId) {
        try {
          const { response, data } = await fetchUserRolesWithPermissionsCached(accessToken, {
            userId,
            roleId: normalizedRoleId,
            force: true,
          }) || {};

          if (response?.ok && data?.success && data?.data?.permissions) {
            applyRolePermissionsState(
              normalizedRoleId,
              data.data.permissions || {}
            );
          } else {
            const { response: responseAll, data: dataAll } = await fetchUserRolesWithPermissionsCached(
              accessToken,
              { userId, force: true }
            ) || {};

            if (responseAll?.ok && dataAll?.success && dataAll?.data?.permissions) {
              applyRolePermissionsState(
                normalizedRoleId,
                dataAll.data.permissions || {}
              );
            } else {
              applyRolePermissionsState(normalizedRoleId, readStoredPermissionsMap());
            }
          }
        } catch (error) {
          console.error('Error fetching permissions for role:', error);
          applyRolePermissionsState(normalizedRoleId, readStoredPermissionsMap());
        }
      } else {
        applyRolePermissionsState(normalizedRoleId, readStoredPermissionsMap());
      }

      dispatchRoleChanged(normalizedRoleId, role);
    }
  };

  const getActiveRolePermissions = () => {
    const roleId = normalizeRoleId(
      activeRole?.id ||
        localStorage.getItem("active_role_id") ||
        localStorage.getItem("user_role")
    );
    if (!roleId) {
      return readStoredUserPermissions();
    }

    const fromState = getPermissionsForRoleId(roleId, rolePermissions);
    if (fromState.length > 0) {
      return fromState;
    }

    const fromStorageMap = getPermissionsForRoleId(roleId, readStoredPermissionsMap());
    if (fromStorageMap.length > 0) {
      return fromStorageMap;
    }

    return readStoredUserPermissions();
  };

  const hasPermission = (permission) => {
    const permissions = getActiveRolePermissions();
    try {
      const { buildPermissionResolver } = require('../utils/permissionKeyResolver');
      const modulesConfig = require('../components/common/modulesConfig').default;
      const resolve = buildPermissionResolver(modulesConfig);
      const canonical = resolve(permission) || permission;
      return permissions.some((p) => (resolve(p) || p) === canonical);
    } catch (error) {
      return permissions.includes(permission);
    }
  };

  const hasAnyPermission = (permissionList) => {
    return permissionList.some((perm) => hasPermission(perm));
  };

  const hasAllPermissions = (permissionList) => {
    const permissions = getActiveRolePermissions();
    return permissionList.every(perm => permissions.includes(perm));
  };

  const isRole = (roleId) => {
    const activeId = normalizeRoleId(
      activeRole?.id ||
        localStorage.getItem("active_role_id") ||
        localStorage.getItem("user_role")
    );
    return activeId === normalizeRoleId(roleId);
  };

  const value = {
    userRoles,
    activeRole,
    rolePermissions,
    isLoading,
    permissionsRevision,
    switchRole,
    getActiveRolePermissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isRole,
    fetchUserRolesAndPermissions,
    refreshRoles: () => fetchUserRolesAndPermissions({ force: true }),
  };

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};

